import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { phoneStyles } from '../../styles/phone-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';

export class PhoneNumberTable extends LitElement {
  static properties = {
    numbers: { type: Array },
    agents: { type: Array },
    loading: { type: Boolean },
    searchQuery: { type: String },
    sortColumn: { type: String },
    sortDirection: { type: String },
    currentPage: { type: Number },
    pageSize: { type: Number },
    totalItems: { type: Number },
    totalPages: { type: Number },
    selectedNumber: { type: Object }
  };

  static styles = [designSystem, componentStyles, phoneStyles, css`:host { display: block; }`];

  constructor() {
    super();
    this.numbers = [];
    this.agents = [];
    this.loading = false;
    this.searchQuery = '';
    this.sortColumn = '';
    this.sortDirection = 'asc';
    this.currentPage = 1;
    this.pageSize = 20;
    this.totalItems = 0;
    this.totalPages = 1;
    this.selectedNumber = null;
    this._searchDebounceTimer = null;
  }

  /* ─── Event Dispatch Helper ─── */

  _dispatch(name, detail = {}) {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  /* ─── Stats ─── */

  _getActiveCount() {
    return this.numbers.filter(n => n.status === 'active').length;
  }

  _getTotalCost() {
    return this.numbers.reduce((sum, n) => sum + n.monthlyCost, 0).toFixed(2);
  }

  /* ─── Search ─── */

  _handleSearch(e) {
    const query = e.target.value;
    clearTimeout(this._searchDebounceTimer);
    this._searchDebounceTimer = setTimeout(() => {
      this._dispatch('search', { query });
    }, 350);
  }

  /* ─── Sort ─── */

  _sortBy(column) {
    this._dispatch('sort', { column });
  }

  _sortIcon(column) {
    if (this.sortColumn !== column) return '';
    return this.sortDirection === 'asc' ? '\u25B2' : '\u25BC';
  }

  /* ─── Pagination ─── */

  _goToPage(p) {
    const page = Math.max(1, Math.min(p, this.totalPages));
    if (page !== this.currentPage) {
      this._dispatch('page-change', { page });
    }
  }

  _renderPageNumbers() {
    const total = this.totalPages;
    const current = this.currentPage;
    const pages = [];
    const range = 2;

    pages.push(1);

    if (current - range > 2) {
      pages.push('...');
    }

    for (let i = Math.max(2, current - range); i <= Math.min(total - 1, current + range); i++) {
      pages.push(i);
    }

    if (current + range < total - 1) {
      pages.push('...');
    }

    if (total > 1) {
      pages.push(total);
    }

    return pages.map(p =>
      p === '...'
        ? html`<span class="page-ellipsis">...</span>`
        : html`
          <button
            class="page-btn ${p === current ? 'active' : ''}"
            @click="${() => this._goToPage(p)}"
          >${p}</button>
        `
    );
  }

  /* ─── Render ─── */

  render() {
    const startIdx = (this.currentPage - 1) * this.pageSize;
    const endIdx = Math.min(startIdx + this.numbers.length, this.totalItems);

    return html`
      ${iconStyles}

      <!-- Page Header -->
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Phone Numbers</h1>
          <p class="page-description">Manage your Twilio phone numbers and routing configuration</p>
        </div>
        <div style="display: flex; gap: var(--space-3);">
          <button class="btn btn-primary" @click=${() => this._dispatch('add-number')}>
            <i class="fa-sharp fa-light fa-plus"></i>
            Add Number
          </button>
        </div>
      </div>

      <!-- Stats Row -->
      <div class="stats-row">
        <div class="stat-card ${this.loading ? 'skeleton-stat' : ''}">
          <div class="stat-icon total">
            <i class="fa-sharp-duotone fa-solid fa-phone-office"></i>
          </div>
          <div class="stat-content">
            <span class="metric-value" style="font-size: var(--text-2xl);">${this.loading ? '—' : this.totalItems}</span>
            <span class="metric-label">Total Numbers</span>
          </div>
        </div>

        <div class="stat-card ${this.loading ? 'skeleton-stat' : ''}">
          <div class="stat-icon active">
            <i class="fa-sharp-duotone fa-solid fa-circle-check"></i>
          </div>
          <div class="stat-content">
            <span class="metric-value" style="font-size: var(--text-2xl);">${this.loading ? '—' : this._getActiveCount()}</span>
            <span class="metric-label">Active Numbers</span>
          </div>
        </div>

        <div class="stat-card ${this.loading ? 'skeleton-stat' : ''}">
          <div class="stat-icon cost">
            <i class="fa-sharp-duotone fa-solid fa-dollar-sign"></i>
          </div>
          <div class="stat-content">
            <span class="metric-value" style="font-size: var(--text-2xl);">${this.loading ? '—' : '$' + this._getTotalCost()}</span>
            <span class="metric-label">Monthly Cost</span>
          </div>
        </div>
      </div>

      <!-- Numbers Table -->
      <div class="table-container">
        <div class="table-header">
          <span class="table-title">All Numbers</span>
          <div class="search-input-wrap">
            <i class="fa-sharp fa-light fa-magnifying-glass"></i>
            <input
              type="text"
              class="search-input"
              placeholder="Search numbers..."
              .value=${this.searchQuery}
              @input=${this._handleSearch}
            />
          </div>
        </div>

        <div class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th class="sortable" @click=${() => this._sortBy('phone')}>
                  Phone Number
                  <span class="sort-icon ${this.sortColumn === 'phone' ? 'active' : ''}">${this._sortIcon('phone')}</span>
                </th>
                <th class="sortable" @click=${() => this._sortBy('label')}>
                  Label / Name
                  <span class="sort-icon ${this.sortColumn === 'label' ? 'active' : ''}">${this._sortIcon('label')}</span>
                </th>
                <th class="sortable" @click=${() => this._sortBy('agent')}>
                  Assigned Agent
                  <span class="sort-icon ${this.sortColumn === 'agent' ? 'active' : ''}">${this._sortIcon('agent')}</span>
                </th>
                <th>Park (UJID)</th>
                <th class="sortable" @click=${() => this._sortBy('status')}>
                  Status
                  <span class="sort-icon ${this.sortColumn === 'status' ? 'active' : ''}">${this._sortIcon('status')}</span>
                </th>
                <th class="sortable" @click=${() => this._sortBy('cost')}>
                  Monthly Cost
                  <span class="sort-icon ${this.sortColumn === 'cost' ? 'active' : ''}">${this._sortIcon('cost')}</span>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              ${this.loading ? html`
                ${[1,2,3,4,5].map(() => html`
                  <tr>
                    <td>
                      <div class="phone-cell">
                        <div class="skeleton-line" style="width:24px;height:24px;border-radius:4px;flex-shrink:0;"></div>
                        <div class="skeleton-line" style="width:120px;"></div>
                      </div>
                    </td>
                    <td><div class="skeleton-line" style="width:100px;"></div></td>
                    <td>
                      <div class="agent-cell">
                        <div class="skeleton-line" style="width:24px;height:24px;border-radius:50%;flex-shrink:0;"></div>
                        <div class="skeleton-line" style="width:90px;"></div>
                      </div>
                    </td>
                    <td><div class="skeleton-line" style="width:60px;"></div></td>
                    <td><div class="skeleton-line" style="width:56px;height:22px;border-radius:10px;"></div></td>
                    <td><div class="skeleton-line" style="width:60px;"></div></td>
                    <td>
                      <div class="actions-cell">
                        <div class="skeleton-line" style="width:30px;height:30px;border-radius:6px;"></div>
                        <div class="skeleton-line" style="width:30px;height:30px;border-radius:6px;"></div>
                        <div class="skeleton-line" style="width:30px;height:30px;border-radius:6px;"></div>
                      </div>
                    </td>
                  </tr>
                `)}
              ` : this.numbers.length === 0 ? html`
                <tr class="empty-row"><td colspan="7">
                  ${this.searchQuery ? 'No numbers match your search' : 'No phone numbers configured yet'}
                </td></tr>
              ` : this.numbers.map(num => html`
                <tr
                  class="${this.selectedNumber?.id === num.id ? 'expanded' : ''}"
                  @click="${() => this._dispatch('toggle-expand', { number: num })}"
                >
                  <td>
                    <div class="phone-cell">
                      <span class="phone-flag">${num.flag}</span>
                      <span class="phone-number">${num.phone}</span>
                    </div>
                  </td>
                  <td>
                    <span class="label-text">${num.label}</span>
                  </td>
                  <td>
                    <div class="agent-cell">
                      <div class="agent-mini-avatar">${num.agent.initials}</div>
                      <span class="agent-name-text">${num.agent.name}</span>
                    </div>
                  </td>
                  <td>
                    <span class="park-text">${num.park}</span>
                  </td>
                  <td>
                    <span class="badge ${num.status === 'active' ? 'badge-success' : 'badge-neutral'}">
                      <span class="status-dot ${num.status === 'active' ? 'active' : 'inactive'}"></span>
                      ${num.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <span class="cost-text">$${num.monthlyCost.toFixed(2)}</span>
                  </td>
                  <td>
                    <div class="actions-cell" @click="${(e) => e.stopPropagation()}">
                      <button class="action-btn" title="Test Call" @click="${() => this._dispatch('test-call', { number: num })}">
                        <i class="fa-sharp fa-light fa-phone-arrow-up-right"></i>
                      </button>
                      <button class="action-btn" title="Edit" @click="${() => this._dispatch('edit-number', { number: num })}">
                        <i class="fa-sharp fa-light fa-pen-to-square"></i>
                      </button>
                      <button class="action-btn" title="Settings" @click="${() => this._dispatch('edit-number', { number: num })}">
                        <i class="fa-sharp fa-light fa-gear"></i>
                      </button>
                      <button class="action-btn danger" title="Delete" @click="${() => this._dispatch('delete-number', { number: num })}">
                        <i class="fa-sharp fa-light fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                ${this.selectedNumber?.id === num.id ? html`
                  <tr class="expand-row">
                    <td colspan="7">
                      <div class="expand-content">
                        <div class="expand-grid">
                          <!-- Inbound Hours -->
                          <div class="expand-section">
                            <span class="expand-section-title">
                              <i class="fa-sharp fa-light fa-clock"></i>
                              Inbound Hours
                            </span>
                            ${num.inboundHours.length > 0 ? html`
                              <div class="hours-list">
                                ${num.inboundHours.map(h => html`
                                  <div class="hours-item">
                                    <span class="hours-day">${h.day}</span>
                                    <span class="hours-time">${h.time}</span>
                                  </div>
                                `)}
                              </div>
                            ` : html`
                              <span class="expand-section-value" style="color: var(--color-text-tertiary);">
                                No hours configured
                              </span>
                            `}
                          </div>

                          <!-- Webhook & Agent -->
                          <div class="expand-section">
                            <span class="expand-section-title">
                              <i class="fa-sharp fa-light fa-globe"></i>
                              Webhook & Agent
                            </span>
                            <div style="display: flex; flex-direction: column; gap: var(--space-2);">
                              <div style="display: flex; align-items: center; gap: var(--space-2);">
                                ${num.twilioSid ? html`
                                  <span class="status-badge active" style="font-size: var(--text-xs);">
                                    <i class="fa-sharp fa-light fa-circle-check"></i> Webhook Active
                                  </span>
                                ` : html`
                                  <span class="status-badge" style="font-size: var(--text-xs); background: var(--color-surface-elevated); color: var(--color-text-tertiary);">
                                    <i class="fa-sharp fa-light fa-circle-minus"></i> Not Configured
                                  </span>
                                `}
                              </div>
                              <div style="display: flex; align-items: center; gap: var(--space-2);">
                                <span style="font-size: var(--text-xs); color: var(--color-text-secondary);">Agent:</span>
                                <select class="agent-select"
                                  @click=${(e) => e.stopPropagation()}
                                  @change=${(e) => this._dispatch('assign-agent', { numberId: num.id, agentId: e.target.value })}>
                                  <option value="0" ?selected=${!num.agentId}>No Agent</option>
                                  ${this.agents.map(a => html`
                                    <option value="${a.id}" ?selected=${a.id === num.agentId}>${a.name}</option>
                                  `)}
                                </select>
                              </div>
                            </div>
                          </div>

                          <!-- Caller ID Settings -->
                          <div class="expand-section">
                            <span class="expand-section-title">
                              <i class="fa-sharp fa-light fa-id-card"></i>
                              Caller ID Settings
                            </span>
                            <div style="display: flex; flex-direction: column; gap: var(--space-2);">
                              <div class="caller-id-row">
                                ${num.callerIdEnabled
                                  ? html`<i class="fa-sharp fa-light fa-circle-check"></i> Enabled`
                                  : html`<i class="fa-sharp fa-light fa-circle-xmark" style="color: var(--color-text-disabled);"></i> Disabled`
                                }
                              </div>
                              ${num.callerIdName ? html`
                                <span class="expand-section-value">
                                  Display Name: <strong>${num.callerIdName}</strong>
                                </span>
                              ` : ''}
                              <span class="expand-section-value" style="color: var(--color-text-tertiary); font-size: var(--text-xs);">
                                Routing: ${num.routingRules || 'Not configured'}
                              </span>
                            </div>
                          </div>

                          <!-- Notes -->
                          <div class="expand-section">
                            <span class="expand-section-title">
                              <i class="fa-sharp fa-light fa-note-sticky"></i>
                              Notes
                            </span>
                            <textarea
                              class="notes-textarea"
                              .value="${num.notes}"
                              placeholder="Add notes about this number..."
                              @click="${(e) => e.stopPropagation()}"
                              @blur="${(e) => this._dispatch('save-notes', { numberId: num.id, notes: e.target.value })}"
                            ></textarea>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ` : ''}
              `)}
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        ${this.totalItems > 0 ? html`
          <div class="pagination-bar">
            <div class="pagination-info">
              Showing <strong>${startIdx + 1}-${endIdx}</strong> of <strong>${this.totalItems.toLocaleString()}</strong>
            </div>
            <div class="pagination-controls">
              <button class="page-btn" ?disabled="${this.currentPage <= 1}" @click="${() => this._goToPage(this.currentPage - 1)}">
                <i class="fa-sharp fa-light fa-chevron-left"></i>
              </button>
              ${this._renderPageNumbers()}
              <button class="page-btn" ?disabled="${this.currentPage >= this.totalPages}" @click="${() => this._goToPage(this.currentPage + 1)}">
                <i class="fa-sharp fa-light fa-chevron-right"></i>
              </button>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }
}

customElements.define('phone-number-table', PhoneNumberTable);
